
import React, { useEffect, useState, useMemo } from 'react';
import { ArrowLeft, User, Calendar, Phone, MapPin, Users, CheckCircle, AlertCircle } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import axiosInstance from '../api/axiosInstance';

const OnlinePaymentLink = () => {

    const location = useLocation();
    // Replace '%3D' with '=' in the raw search string to handle both cases
    const fixedSearch = location.search.replace('%3D', '=');
    const query = new URLSearchParams(fixedSearch);
    const det = query.get('det');

    console.log(JSON.parse(atob(det)));
    // Memoize detDecoded to prevent new object creation on every render
    const detDecoded = useMemo(() => {
        try {
            return det ? JSON.parse(atob(det)) : null;
        } catch (error) {
            console.error('Error decoding det:', error);
            return null;
        }
    }, [det]);

    const [cart, setCart] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [paymentMessage, setPaymentMessage] = useState('');
    const [showPaymentStatusModal, setShowPaymentStatusModal] = useState(false);

    useEffect(() => {
        const getToken = async () => {
            try {
                const res = await axios.post(`${process.env.REACT_APP_KIOSK_OPS_URL}/validateUserPassword?userName=admin&password=$$Kims@12`);
                localStorage.setItem("token", res.data.tokenNo || "");
                localStorage.setItem("departmentId", res.data.departmentId || "");
            } catch (error) {
                console.error("Error validating user:", error);
            }
        };
        getToken();
    }, []);

    useEffect(() => {
        if (detDecoded && detDecoded.serviceList) {
            const initialCart = detDecoded.serviceList.map(service => ({
                serviceId: service.serviceId,
                serviceName: service.serviceName,
                hospitalTariff: parseFloat(service.amount) / parseInt(service.quantity),
                quantity: parseInt(service.quantity),
                // category: service.serviceName.includes('BED') ? 'Bed Charges' : service.serviceName.includes('ECHO') ? 'Imaging Charges' : 'Lab Charges'
            }));

            // Only update cart if it has changed
            setCart(prevCart => {
                if (JSON.stringify(prevCart) !== JSON.stringify(initialCart)) {
                    return initialCart;
                }
                return prevCart;
            });

            const total = initialCart.reduce((sum, item) => sum + (item.hospitalTariff * item.quantity), 0);
            setTotalAmount(prevTotal => (prevTotal !== total ? total : prevTotal));
        }
    }, [detDecoded]);

    const handlePayment = async () => {
        if (cart.length === 0) {
            setPaymentStatus('failure');
            setPaymentMessage('Please add services to the cart before proceeding to payment.');
            setShowPaymentStatusModal(true);
            return;
        }

        setLoading(true);
        try {
            const response = await axiosInstance.post('/api/patient/create-service-order', {
                patientMrno: detDecoded?.admissionDetails?.mrno,
                services: cart.map(item => ({
                    serviceId: item.serviceId,
                    serviceName: item.serviceName,
                    hospitalTariff: item.hospitalTariff,
                    quantity: item.quantity
                })),
                totalAmount
            }, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            const { key, amount, currency, order_id } = response.data;

            const form = document.createElement('form');
            form.method = 'POST';
            form.action = 'https://api.razorpay.com/v1/checkout/embedded';

            const formFields = {
                key_id: key,
                amount: amount.toString(),
                currency,
                order_id,
                name: "KIMS",
                description: "Service Payment",
                image: "/logo.jpg",
                callback_url: `${axiosInstance.defaults.baseURL}/api/patient/verify-payment`,
                cancel_url: `${axiosInstance.defaults.baseURL}/api/patient/cancel-payment`,
                prefill: {
                    name: detDecoded?.admissionDetails?.patientName || 'Test User',
                    email: 'test@gmail.com',
                    contact: detDecoded?.admissionDetails?.mobileNo || '9427388748',
                },
                theme: { color: "#0FA968" },
                notes: {
                    service_order: true,
                },
            };

            Object.keys(formFields).forEach((key) => {
                if (typeof formFields[key] === 'object') {
                    Object.keys(formFields[key]).forEach((subKey) => {
                        const input = document.createElement('input');
                        input.type = 'hidden';
                        input.name = `${key}[${subKey}]`;
                        input.value = formFields[key][subKey];
                        form.appendChild(input);
                    });
                } else {
                    const input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = key;
                    input.value = formFields[key];
                    form.appendChild(input);
                }
            });

            document.body.appendChild(form);
            form.submit();
            document.body.removeChild(form);
        } catch (err) {
            setPaymentStatus('failure');
            setPaymentMessage('Error creating order: ' + (err.response?.data?.error || err.message || 'Unknown error'));
            setShowPaymentStatusModal(true);
            console.error('Error creating order:', err);
        } finally {
            setLoading(false);
        }
    };

    const renderPaymentStatusModal = () => {
        if (!paymentStatus || paymentStatus === 'success') return null;
        const statusConfig = {
            failure: { bg: "bg-red-50", border: "border-red-200", text: "text-red-800", icon: "❌", title: "Payment Failed" },
            cancel: { bg: "bg-yellow-50", border: "border-yellow-200", text: "text-yellow-800", icon: "⚠️", title: "Payment Cancelled" }
        };
        const config = statusConfig[paymentStatus];
        // Helper to close and reset modal state
        const closeModal = () => {
            setShowPaymentStatusModal(false);
            setPaymentStatus(null);
            setPaymentMessage("");
        };
        return (
            <div className="fixed inset-0 z-50 flex justify-center items-center w-full h-full bg-black bg-opacity-50 overflow-y-auto">
                <div className="relative p-4 w-full max-w-md bg-white rounded-2xl shadow-2xl">
                    <button
                        onClick={closeModal}
                        className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    <div className={`p-4 border-2 rounded-xl ${config.bg} ${config.border} text-center`}>
                        <span className="text-3xl mb-3 block">{config.icon}</span>
                        <h3 className={`font-bold text-lg ${config.text}`}>{config.title}</h3>
                        <p className={`mt-2 ${config.text} text-sm opacity-90`}>{paymentMessage}</p>
                        <div className="mt-4 flex justify-center gap-3">
                            <button
                                onClick={closeModal}
                                className="px-4 py-1.5 bg-[#0FA968] text-white font-medium text-sm rounded-lg shadow hover:bg-[#138F5E] transition duration-200"
                            >
                                Try Again
                            </button>
                            <button
                                onClick={closeModal}
                                className="px-4 py-1.5 bg-gray-200 text-gray-800 font-medium text-sm rounded-lg shadow hover:bg-gray-300 transition duration-200"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-4xl mx-auto px-4 py-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <Link to="/" className="inline-flex items-center px-3 py-1.5 bg-emerald-600 text-white font-medium text-sm rounded-lg hover:bg-emerald-700 transition-colors duration-200 shadow-sm">
                                <ArrowLeft className="w-4 h-4 mr-1" />
                                Back
                            </Link>
                            <h1 className="text-lg font-semibold text-gray-900">Online Payment</h1>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 py-6">
                {detDecoded && (
                    <div className="flex flex-col gap-4">
                        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                            <div className="bg-emerald-50 border-b border-emerald-100 px-4 py-2">
                                <div className="flex items-center">
                                    <CheckCircle className="w-4 h-4 text-emerald-600 mr-2" />
                                    <p className="text-emerald-800 font-medium text-sm">Patient Details</p>
                                </div>
                            </div>
                            <div className="p-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-lg flex items-center justify-center">
                                            <User className="w-6 h-6 text-emerald-600" />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-900">
                                                {detDecoded.admissionDetails.patientName}
                                            </h3>
                                            <p className="text-xs text-gray-500">MRNO: {detDecoded.admissionDetails.mrno}</p>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex items-center">
                                            <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                                            <div>
                                                <p className="text-xs text-gray-500">DOB</p>
                                                <p className="text-sm font-medium text-gray-900">
                                                    {detDecoded.admissionDetails.dob}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center">
                                            <Phone className="w-4 h-4 text-gray-400 mr-2" />
                                            <div>
                                                <p className="text-xs text-gray-500">Mobile</p>
                                                <p className="text-sm font-medium text-gray-900">
                                                    {detDecoded.admissionDetails.mobileNo}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex items-center">
                                            <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                                            <div>
                                                <p className="text-xs text-gray-500">Location</p>
                                                <p className="text-sm font-medium text-gray-900">
                                                    {detDecoded.admissionDetails.patientLocation}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center">
                                            <Users className="w-4 h-4 text-gray-400 mr-2" />
                                            <div>
                                                <p className="text-xs text-gray-500">Consultant</p>
                                                <p className="text-sm font-medium text-gray-900">
                                                    {detDecoded.admissionDetails.consultantName}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex items-center">
                                            <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                                            <div>
                                                <p className="text-xs text-gray-500">Admission Date</p>
                                                <p className="text-sm font-medium text-gray-900">
                                                    {detDecoded.admissionDetails.admissionDate}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center">
                                            <Users className="w-4 h-4 text-gray-400 mr-2" />
                                            <div>
                                                <p className="text-xs text-gray-500">Admission No / Visit No</p>
                                                <p className="text-sm font-medium text-gray-900">
                                                    {detDecoded.admissionDetails.admissionNo} / {detDecoded.admissionDetails.visitNo}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex items-center">
                                            <Users className="w-4 h-4 text-gray-400 mr-2" />
                                            <div>
                                                <p className="text-xs text-gray-500">IP Status</p>
                                                <p className="text-sm font-medium text-gray-900">
                                                    {detDecoded.admissionDetails.ipStatus}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center">
                                            <Users className="w-4 h-4 text-gray-400 mr-2" />
                                            <div>
                                                <p className="text-xs text-gray-500">Order No</p>
                                                <p className="text-sm font-medium text-gray-900">
                                                    {detDecoded.orderNo}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border p-4">
                            <div className="space-y-3">
                                {cart.length === 0 ? (
                                    <p className="text-sm text-gray-500 text-center">No services added</p>
                                ) : (
                                    <>
                                        {cart.map(item => (
                                            <div key={item.serviceId} className="flex justify-between items-center border-b pb-2">
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900">{item.serviceName}</p>
                                                    {/* <p className="text-xs text-gray-500">{item.category}</p> */}
                                                    <p className="text-xs text-gray-500">Quantity: {item.quantity}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-sm font-semibold text-emerald-600">
                                                        ₹{item.hospitalTariff} x {item.quantity}
                                                    </p>
                                                    <p className="text-sm font-semibold text-emerald-600">
                                                        ₹{(item.hospitalTariff * item.quantity).toFixed(2)}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                        <div className="border-t pt-3">
                                            <div className="flex justify-between items-center">
                                                <p className="text-sm font-semibold text-gray-900">Total Amount</p>
                                                <p className="text-base font-bold text-emerald-600">₹{totalAmount.toFixed(2)}</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={handlePayment}
                                            className={`w-full mt-3 py-2 bg-emerald-600 text-white font-medium text-sm rounded-lg shadow hover:bg-emerald-700 transition duration-200 ${cart.length === 0 || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            disabled={cart.length === 0 || loading}
                                        >
                                            {loading ? (
                                                <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                                            ) : (
                                                'Proceed to Payment'
                                            )}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {!detDecoded && (
                    <div className="bg-white rounded-xl shadow-sm border p-4">
                        <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg">
                            <AlertCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0" />
                            <p className="text-red-700 text-sm">Invalid or missing payment details</p>
                        </div>
                    </div>
                )}
                {renderPaymentStatusModal()}
            </div>
        </div>
    );
};

export default OnlinePaymentLink;